/* eslint-disable max-lines */
/* eslint-disable no-param-reassign */
import { api } from "jiffy-api";
import { createJotaiPiniaFacadeForReact } from "common/vue/createJotaiPiniaFacadeForReact";
import {
  jotaiStore,
  SPANGLE_MODES,
  lastSpangledDimensionsAtom,
  spangleVariationsAtom,
  spangleModeAtom,
  selectedAlgorithmAtom,
  originalArtworkUrlAtom,
  spanglesMaxImageCountAtom,
  allAPIsCompletedStateAtom,
  invalidSpangleSelectedAtom,
  isSpangleReorderingAtom,
  completedSpangleDataAtom
} from "./spangles-module.atoms";
// eslint-disable-next-line import/no-cycle
import { dtfStore } from "./store";

export { SPANGLE_MODES };

const lastSpangledDimensionsDefault = { width: null, height: null };
const defaultVariation = { isLoading: true };

// eslint-disable-next-line no-unused-vars
const initialState = {
  lastSpangledDimensions: lastSpangledDimensionsDefault,
  spangleVariations: [],
  spangleMode: SPANGLE_MODES.spangleIt,
  selectedAlgorithm: null,
  originalArtworkUrl: "",
  spanglesMaxImageCount: 1,
  allAPIsCompletedState: true,
  invalidSpangleSelected: false,
  isSpangleReordering: false,
  completedSpangleData: []
};

export const useSpanglesStore = createJotaiPiniaFacadeForReact({
  jotaiStore,

  state: {
    lastSpangledDimensions: lastSpangledDimensionsAtom,
    spangleVariations: spangleVariationsAtom,
    spangleMode: spangleModeAtom,
    selectedAlgorithm: selectedAlgorithmAtom,
    originalArtworkUrl: originalArtworkUrlAtom,
    spanglesMaxImageCount: spanglesMaxImageCountAtom,
    allAPIsCompletedState: allAPIsCompletedStateAtom,
    invalidSpangleSelected: invalidSpangleSelectedAtom,
    isSpangleReordering: isSpangleReorderingAtom,
    completedSpangleData: completedSpangleDataAtom
  },

  getters: {
    spangleIt() {
      return this.spangleMode === SPANGLE_MODES.spangleIt;
    },
    spangleSizeEditing() {
      return this.spangleMode === SPANGLE_MODES.editSize;
    },
    spangleSizeEdited() {
      return this.spangleMode === SPANGLE_MODES.sizeEdited;
    },
    spanglesLoading() {
      return this.spangleMode === SPANGLE_MODES.spanglesLoading;
    },
    isInitialSpangle() {
      return this.spangleVariations.length === 0;
    },
    allAPIsCompleted() {
      return this.spangleVariations.every(variation => !variation.isLoading);
    },
    allAPIsFailed() {
      return this.spangleVariations.every(variation => !variation.isLoading && variation.error);
    },
    isSelectedVersionLoading() {
      const selectedVersion = this.spangleVariations.find(
        variation => variation.algorithm === this.selectedAlgorithm
      );
      return selectedVersion?.isLoading;
    },
    selectedVersion() {
      return this.spangleVariations.find(
        variation => variation.algorithm === this.selectedAlgorithm
      );
    },
    isFirstSpangle() {
      return this.spangleVariations.length === 0;
    }
  },

  actions: {
    setSpangleMode(mode) {
      this.spangleMode = mode;
    },
    setSpangleVariations(variations) {
      this.spangleVariations = variations;
    },
    setLastSpangledDimensions(dimensions) {
      this.lastSpangledDimensions = { ...dimensions };
    },
    setSelectedAlgorithm(algorithm) {
      this.selectedAlgorithm = algorithm;
    },
    setOriginalArtworkUrl(url) {
      this.originalArtworkUrl = url;
    },
    setInvalidSpangleSelected(invalid) {
      this.invalidSpangleSelected = invalid;
    },
    setIsSpangleReordering(isSpangleReordering) {
      this.isSpangleReordering = isSpangleReordering;
    },
    resetSpangles() {
      this.$reset();
    },
    addCompletedSpangleData(data) {
      this.completedSpangleData.push(data);
    },
    resetCompletedSpangleData() {
      this.completedSpangleData = [];
    },
    selectVersion({ version }) {
      if (!version) return;
      const store = dtfStore;
      this.setSelectedAlgorithm(version.algorithm);

      if (version.isLoading) return;

      this.setInvalidSpangleSelected(version.error);
      const imageUrl = version.error ? this.originalArtworkUrl : version.imageUrl;

      store.updateArtworkValues({
        index: 0,
        spangleCsv: version.spangleCsv,
        spanglePagesInfo: version.spanglePagesInfo,
        spanglePositionsJson: version.positionsJson,
        spangledImageUrl: imageUrl
      });
    },
    prepareVariations({ isRetry }) {
      const {
        spangleVariations,
        selectedAlgorithm,
        allAPIsFailed,
        isFirstSpangle,
        isSpangleReordering
      } = this;
      const { spangleAlgorithms } = dtfStore;

      let variationsObject = [];

      if (isRetry) {
        variationsObject = spangleVariations.map(variation => {
          if (variation.algorithm === selectedAlgorithm || allAPIsFailed) {
            return {
              ...defaultVariation,
              algorithm: variation.algorithm,
              spanglePagesInfo: variation.spanglePagesInfo
            };
          }
          return variation;
        });
      } else {
        const isFreshCall = isFirstSpangle || isSpangleReordering;
        const algorithmList = isFreshCall ? spangleAlgorithms : spangleVariations;
        variationsObject = algorithmList.map(item => ({
          ...(isFreshCall ? defaultVariation : item),
          algorithm: isFreshCall ? item : item.algorithm,
          spanglePagesInfo: isFreshCall ? null : item.spanglePagesInfo,
          isLoading: true
        }));
      }

      if (!selectedAlgorithm || isSpangleReordering) {
        this.setSelectedAlgorithm(variationsObject[0].algorithm);
      }

      this.setSpangleVariations(variationsObject);
    },
    updateSpangleVersion({ spangle }) {
      const { spangleVariations } = this;

      const updatedList = spangleVariations.map(variation =>
        variation.algorithm === spangle.algorithm
          ? { ...variation, ...spangle, isLoading: false }
          : variation
      );

      this.setSpangleVariations(updatedList);
    },
    // eslint-disable-next-line max-lines-per-function
    changeColor({ colorValues }) {
      const variationToUpdate = this.spangleVariations.filter(
        ({ isLoading, error, spanglePagesInfo }) => {
          const variationColorCount = spanglePagesInfo
            ? Object.values(spanglePagesInfo).filter(color => typeof color === "object").length
            : 0;

          const colorValuesCount = Object.keys(colorValues).length;

          return !isLoading && !error && colorValuesCount === variationColorCount;
        }
      );

      if (variationToUpdate.length === 0) return;

      /* eslint-disable camelcase */
      // eslint-disable-next-line max-lines-per-function
      variationToUpdate.map(variation => {
        const { total_beads_count, time_taken } = variation.spanglePagesInfo;

        return api.dtf
          .updateSpangleColor({
            positions_json: variation.positionsJson,
            pages_info: {
              ...colorValues,
              total_beads_count,
              time_taken
            }
          })
          .then(result => {
            const store = dtfStore;
            const imageUrl = result.design_url || result.design_base64;
            const hasValidImage = Boolean(imageUrl);
            const effectiveImageUrl = hasValidImage ? imageUrl : variation.imageUrl;

            this.updateSpangleVersion({
              spangle: {
                ...variation,
                imageUrl: effectiveImageUrl,
                ...(result.pages_info ? { spanglePagesInfo: result.pages_info } : {}),
                ...(result.positions_json ? { positionsJson: result.positions_json } : {}),
                error: !effectiveImageUrl
              }
            });

            if (variation.algorithm === this.selectedAlgorithm && hasValidImage) {
              store.updateArtworkValues({
                index: 0,
                spangled: true,
                spangledImageUrl: effectiveImageUrl,
                ...(result.pages_info ? { spanglePagesInfo: result.pages_info } : {}),
                ...(result.positions_json ? { spanglePositionsJson: result.positions_json } : {})
              });
            }
          })
          .catch(error => {
            window.Honeybadger?.notify("DTF spangle color change error", {
              params: {
                error_message: error.message,
                algorithm: variation.algorithm,
                pages_info: colorValues
              }
            });
          });
      });
      /* eslint-enable camelcase */
    },
    getVariation({
      algorithm,
      spanglePagesInfo,
      id,
      algorithmsSize,
      algorithmToAnimate,
      processTimeStamp
    }) {
      const { artworks, cable } = dtfStore;

      /* eslint-disable no-underscore-dangle */
      cable.spangle({
        id,
        version_identifier: algorithm,
        pages_info: spanglePagesInfo,
        algorithms_size: algorithmsSize,
        algorithm_to_animate: algorithmToAnimate,
        process_time_stamp: processTimeStamp,
        width: artworks[0].width,
        height: artworks[0].height
      });
      /* eslint-enable no-underscore-dangle */
    },
    /* eslint-enable max-lines-per-function */
    getSpangleVariations({ isRetry, processTimeStamp }) {
      this.resetCompletedSpangleData();
      const { artworks, spangleAlgorithms } = dtfStore;

      if (!spangleAlgorithms.length) {
        return;
      }

      const { id } = artworks[0];

      this.prepareVariations({ isRetry });

      this.setSpangleMode(SPANGLE_MODES.spanglesLoading);
      this.setInvalidSpangleSelected(false);
      this.setIsSpangleReordering(false);

      const algorithmsToCall = this.spangleVariations.filter(({ isLoading }) => isLoading);

      algorithmsToCall.map(({ algorithm, spanglePagesInfo }) =>
        this.getVariation({
          algorithm,
          spanglePagesInfo,
          id,
          algorithmsSize: algorithmsToCall.length,
          algorithmToAnimate: algorithmsToCall[0].algorithm,
          processTimeStamp
        })
      );
    },
    showSpangleResult() {
      const { artworks, updateArtworkValues } = dtfStore;

      this.setLastSpangledDimensions({
        width: artworks[0]?.width,
        height: artworks[0]?.height
      });

      this.completedSpangleData.forEach(data => {
        this.updateSpangleVersion({ spangle: data });
      });

      // We want to select the first successful spangled algorithm
      const firstSuccessfuleSpangle = this.spangleVariations.find(data => !data.error);
      this.selectVersion({ version: firstSuccessfuleSpangle });

      updateArtworkValues({ index: 0, isLoading: false });
    },
    cancelSpangleProcess({ artwork }) {
      const { lastSpangledDimensions, spangleVariations, updateArtworkValuesById } = dtfStore;
      const updatedList = spangleVariations.map(variation => ({ ...variation, isLoading: false }));

      this.setSpangleVariations(updatedList);
      updateArtworkValuesById({
        id: artwork.id,
        width: parseFloat(lastSpangledDimensions.width),
        height: parseFloat(lastSpangledDimensions.height)
      });

      this.setSpangleMode(SPANGLE_MODES.sizeEdited);
      this.resetCompletedSpangleData();
    },
    async cancelArtworkProcess({ artwork }) {
      api.dtf.cancelArtworkProcess({ id: artwork.id });
      this.setSpangleMode(SPANGLE_MODES.sizeEdited);
      this.resetCompletedSpangleData();
    }
  }
});

export const spanglesStore = useSpanglesStore.store;
